import useWindowWidth from "./useWindowWidth";
import { useAppSelector } from "../app/hooks";

/**
 * Whether to lay the onboarding out as a phone:
 *
 * 1. Crypto in the app's desktop modal: the app says mobile=false.
 * 2. Crypto full screen in the app's phone layout: the app says mobile=true.
 * 3. Crypto's QR link (/dni, /identidad), opened on its own in the phone's
 *    browser: no app to say it, so by width.
 * 4. Any other onboarding: by width, as always.
 *
 * Our own width cannot tell 1 from 2: the 560px modal would read as a phone.
 * `isCryptoAppModal` is case 1, `isCryptoFullScreen` case 2 and
 * `isCryptoOutsideApp` case 3.
 */
const useMobileLayout = () => {
  // As useRemoteParams reads it, but without the router: Container uses this
  // hook, and MaintenanceScreen renders one outside the RouterProvider.
  const { onboardingType: savedTemplate } = useAppSelector(
    (state) => state.activeTemplate,
  );
  const onboardingType =
    savedTemplate ||
    new URLSearchParams(window.location.search).get("template");
  const { isMobileLayout } = useAppSelector((state) => state.crypto);
  const { width } = useWindowWidth();

  // The crypto (Allaria Digital) onboarding.
  const isCrypto = onboardingType === "DIGITAL";
  // Lay it out as a phone: crypto in the app, as the app says; else by width.
  const isMobile =
    isCrypto && isMobileLayout !== null ? isMobileLayout : width < 768;
  // Crypto in the app's desktop modal (case 1).
  const isCryptoAppModal = isCrypto && isMobileLayout === false;
  // Crypto full screen in the app's phone layout (case 2).
  const isCryptoFullScreen = isCrypto && isMobileLayout === true;
  // Crypto's QR link, opened outside the app (case 3).
  const isCryptoOutsideApp = isCrypto && isMobileLayout === null;

  return {
    isCrypto,
    isMobile,
    isCryptoAppModal,
    isCryptoFullScreen,
    isCryptoOutsideApp,
  };
};

export default useMobileLayout;
